import React, {FC} from 'react'
import {useExperiments, useTranslation, WidgetProps} from '@wix/yoshi-flow-editor'
import {Text} from 'wix-ui-tpa/cssVars'
import {useSettings, useStyles} from '@wix/tpa-settings/react'

import {withReservationAddOnStorageProvider} from '../storage'
import {stylesParams} from '../stylesParams'
import {settingsParams} from '../settingsParams'
import {FormLayout, TextAlignment} from '../../../types/createStylesParams'
import {RtlProvider} from '../../../components-shared/RtlProvider'
import {LayoutSize} from '../../../components-shared/LayoutSizeProvider/types'
import {withLayoutSizeProvider} from '../../../components-shared/LayoutSizeProvider'
import {useDynamicImport} from '../../../utils/useDynamicImport'
import {EXPERIMENTS} from '../../../utils/constants'
import {ErrorMessage} from '../../../components-shared/ErrorMessage'

import {LAYOUT_BREAKPOINTS_START} from './constants'
import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'
import {Form} from './components/Form'

const Widget: FC<WidgetProps> = () => {
  const styles = useStyles()
  const settings = useSettings()
  const {
    shouldShowNoLocations,
    enabledReservationLocations,
    layoutSize,
    containerWidth,
    shouldShowAutoApproveText,
    getTimeSlotsRequestFailed,
    handleErrorClick,
  } = useHooks()
  const {t} = useTranslation()
  const {experiments} = useExperiments()
  const isErrorHandlerIntegrationEnabled = experiments.enabled(EXPERIMENTS.errorHandlerIntegration)

  const showTitle = settings.get(settingsParams.showTitle)
  const showDescription = settings.get(settingsParams.showDescription)
  const textAlignment = settings.get(settingsParams.textAlignment)
  const titleText = settings.get(
    shouldShowAutoApproveText ? settingsParams.titleText : settingsParams.titleRequestText,
  )
  const titleTag =
    styles.get(stylesParams.titleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.titleFont).htmlTag

  const descriptionText = settings.get(
    shouldShowAutoApproveText
      ? settingsParams.descriptionText
      : settingsParams.descriptionRequestText,
  )
  const descriptionTag =
    styles.get(stylesParams.subtitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.subtitleFont).htmlTag

  const textAlignmentClass = TextAlignmentToClass[textAlignment]

  const formLayout = settings.get(settingsParams.formLayout)

  const formLayoutClass =
    formLayout === FormLayout.Packed || layoutSize === LayoutSize.XS || layoutSize === LayoutSize.S
      ? classes.packed
      : classes.strip

  const NotAcceptingBanner = useDynamicImport(
    () => import('../../Reservations/components/NotAcceptingBanner'),
  )

  if (shouldShowNoLocations) {
    return (
      <RtlProvider>
        <div className={st(classes.root, {[layoutSize]: true})}>
          {NotAcceptingBanner ? (
            <NotAcceptingBanner layoutSize={layoutSize} className={classes.notAcceptingBanner} />
          ) : (
            <div />
          )}
        </div>
      </RtlProvider>
    )
  }

  return (
    <RtlProvider>
      <div
        aria-label={t('uou-reservations.add-on.aria-label')}
        className={st(classes.root, {[layoutSize]: true}, formLayoutClass)}
      >
        {getTimeSlotsRequestFailed && (
          <ErrorMessage
            message={
              isErrorHandlerIntegrationEnabled
                ? t('uou-reservations.reservations.get-time-slots-error')
                : t('uou-reservations.reservations.refresh-page-error')
            }
            onClick={handleErrorClick}
          />
        )}
        {showTitle ? (
          <Text tagName={titleTag} className={st(classes.title, textAlignmentClass)}>
            {titleText}
          </Text>
        ) : null}

        {showTitle && showDescription ? (
          <div className={classes.spacerBetweenTitleAndDescription} />
        ) : null}

        {showDescription ? (
          <Text tagName={descriptionTag} className={st(classes.subtitle, textAlignmentClass)}>
            {descriptionText}
          </Text>
        ) : null}

        {showTitle || showDescription ? <div className={classes.spacerBetweenTextAndForm} /> : null}

        <Form
          enabledReservationLocations={enabledReservationLocations}
          layoutSize={layoutSize}
          containerWidth={containerWidth}
          showAutoApproveButtonLabel={shouldShowAutoApproveText}
        />
      </div>
    </RtlProvider>
  )
}

const TextAlignmentToClass: Record<TextAlignment, string> = {
  [TextAlignment.Left]: classes.textLeft,
  [TextAlignment.Center]: classes.textCenter,
  [TextAlignment.Right]: classes.textRight,
}

export default withLayoutSizeProvider(
  withReservationAddOnStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
  classes.rootWrapper,
)
